import * as _ from 'underscore';
import { UrlParamKeys } from '../app/UrlParamKeys';
import { Tableau } from '../common/utilities/TableauLog';
import { ServerSettingsTabsCtrl } from '../settings/controllers/ServerSettingsTabsCtrl';
import { ISettingsCoordinatorCtrlScope } from '../settings/directives/tbSettingsCoordinator';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../settings/interfaces/ISite';
import { ISiteSettingsForSiteAdmin } from '../settings/interfaces/ISiteSettingsForSiteAdmin';
import { UserSettingsActions } from './actions/UserSettingsActions';
import { AddSiteCtrl } from './controllers/AddSiteCtrl';
import { AddUserToDataAlertCtrl } from './controllers/AddUserToDataAlertCtrl';
import { AdminSiteStatusCtrl } from './controllers/AdminSiteStatusCtrl';
import { AdminVizViewerCtrl } from './controllers/AdminVizViewerCtrl';
import { AuthenticateConnectionCtrl, ConnectionAuthenticationStateParams } from './controllers/AuthenticateConnectionCtrl';
import { BackgroundJobsCtrl } from './controllers/BackgroundJobsCtrl';
import { ContentSubplaceCtrl, IContentSubplaceResolve } from './controllers/ContentSubplaceCtrl';
import { ErrorCtrl, IErrorStateParams } from './controllers/ErrorCtrl';
import { GroupDetailsCtrl } from './controllers/GroupDetailsCtrl';
import { GroupPlaceCtrl } from './controllers/GroupPlaceCtrl';
import { GroupsPlaceCtrl } from './controllers/GroupsPlaceCtrl';
import { GroupUsersSubplaceCtrl } from './controllers/GroupUsersSubplaceCtrl';
import { InitializationCtrl } from './controllers/InitializationCtrl';
import { LicensesCtrl } from './controllers/LicensesCtrl';
import { LoginCtrl } from './controllers/LoginCtrl';
import { LoginSitePickerCtrl } from './controllers/LoginSitePickerCtrl';
import { MainCtrl } from './controllers/MainCtrl';
import { PasswordResetCtrl } from './controllers/PasswordResetCtrl';
import { PKCEGenerateCodeChallengeCtrl } from './controllers/PKCEGenerateCodeChallengeCtrl';
import { PKCEGetAuthorizationCodeCtrl } from './controllers/PKCEGetAuthorizationCodeCtrl';
import { PKCELoginCtrl } from './controllers/PKCELoginCtrl';
import { RemoveUserFromDataAlertCtrl } from './controllers/RemoveUserFromDataAlertCtrl';
import { RequestPasswordResetCtrl } from './controllers/RequestPasswordResetCtrl';
import { SchedulePlaceCtrl } from './controllers/SchedulePlaceCtrl';
import { SchedulesCtrl } from './controllers/SchedulesCtrl';
import { ServerStatusCtrl } from './controllers/ServerStatusCtrl';
import { ServerUserPlaceCtrl } from './controllers/ServerUserPlaceCtrl';
import { ServerUserSettingsSubplaceCtrl } from './controllers/ServerUserSettingsSubplaceCtrl';
import { ServerUsersPlaceCtrl } from './controllers/ServerUsersPlaceCtrl';
import { setBrowserTitleCtrl } from './controllers/SetBrowserTitleCtrl';
import { SignedOutCtrl } from './controllers/SignedOutCtrl';
import { SignInErrorCtrl } from './controllers/SigninErrorCtrl';
import { SiteAuthenticationSettingsCtrl } from './controllers/SiteAuthenticationSettingsCtrl';
import { SiteSettingsTabsCtrl } from './controllers/SiteSettingsTabsCtrl';
import { SitesPlaceCtrl } from './controllers/SitesPlaceCtrl';
import { SiteUsersPlaceCtrl } from './controllers/SiteUsersPlaceCtrl';
import { TasksCtrl } from './controllers/TasksCtrl';
import { VizViewerCtrl } from './controllers/VizViewerCtrl';
import { WorkbookRevisionVizViewerCtrl } from './controllers/WorkbookRevisionVizViewerCtrl';
import { FeatureFlags } from './FeatureFlags';
import { Helpers } from './helpers/Helpers';
import { SubplaceStateConfigHelper } from './helpers/SubplaceStateConfigHelper';
import { L10n } from './L10n/L10n';
import { IGroupInfo } from './models/IGroupInfo';
import { Place } from './models/IPlace';
import { IUserInfo } from './models/IUserInfo';
import { SitesPlace } from './models/SitesPlace';
import { TaskPlace } from './models/TaskPlace';
import { Module } from './Module';
import { Groups } from './resources/Groups';
import { Schedules } from './resources/Schedules';
import { IServerTimestamps, ServerSettings } from './resources/ServerSettings';
import { ServerUsers } from './resources/ServerUsers';
import { Sites } from './resources/Sites';
import { GetDataConnectionAuthInfoRequest } from './ServerApi/requests/GetDataConnectionAuthInfoRequest';
import { IEditableServerSettings, IExtensionsServerSettings } from './ServerApi/types/IEditableServerSettings';
import { IGroup } from './ServerApi/types/IGroup';
import { ISchedule } from './ServerApi/types/ISchedule';
import { ErrorState } from './services/ErrorState';
import { GettingStartedNotifications } from './services/GettingStartedNotifications';
import { LicensesService } from './services/LicensesService';
import { MastheadNotifications } from './services/MastheadNotifications';
import { PostLoginNotifications } from './services/PostLoginNotifications';
import { RebuildSearchIndexService } from './services/RebuildSearchIndexService';
import { ServerInfoService } from './services/ServerInfoService';
import { AuthLevel, ServerService } from './services/ServerService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from './services/SiteAuthenticationSettingsService';
import { SiteUrlMatcherFactory } from './services/SiteUrlMatcherFactory';

export interface UserStateParams {
  domainName: string;
  username: string;
}

Module.instance().config(<any[]>[
  '$httpProvider',
  '$sceProvider',
  '$stateProvider',
  '$urlRouterProvider',
  '$localForageProvider',
  'SiteUrlMatcherFactoryProvider',
  'ApiPrefix',
  (
    $httpProvider: ng.IHttpProvider,
    $sceProvider: any,
    $stateProvider: ng.ui.IStateProvider,
    $urlRouterProvider: ng.ui.IUrlRouterProvider,
    $localForageProvider: angular.localForage.ILocalForageProvider,
    siteUrlMatcherFactory: SiteUrlMatcherFactory,
    ApiPrefix: string
  ) => {

    $localForageProvider.config({storeName: 'vizportal'});

    // Make ui-router's internal state object accessible on the state config.
    // This is hackery, but community endorsed hackery. To limit our dependence
    // on this, the $$state object should never be accessed by anything outside
    // of the StateWrapperService, so it should not be added to the typescript
    // definition
    $stateProvider.decorator('parent', (state: any, parent: Function) => {
      state.self.$$state = state;

      // B147816. This adds a resolve to every state that waits for all pending session info requests (like a
      // switch site) to finish before continuing with the state transition. We don't want to let the
      // controllers act on stale session data.
      const getSessionInfoPromise = ['ServerService', (server: ServerService) => {
        // Swallow any errors. We just want to know when it's done.
        return server.getSessionInfoPromise().catch(angular.noop);
      }];
      const resolve = _.extend({getSessionInfoPromise : getSessionInfoPromise}, state.self.resolve);
      state.self.resolve = resolve;
      state.resolve = state.self.resolve;
      return parent(state);
    });

    function goToFatalErrorState($injector: ng.auto.IInjectorService, message: string){
      Tableau.Logger().error(message);
      // use the state.go instead of a location.path. You can't directly call $state because circular dependency. See
      // https://stackoverflow.com/questions/26594763/angularjs-redirect-not-occurring-in-interceptor
      const stateManager: any = $injector.get('$state'); // no typing exists for this method :(
      stateManager.go('fatal');
    }

    $httpProvider.interceptors.push(['$q', '$injector', ($q, $injector) => {
      const apiRequestRegex = new RegExp('^' + ApiPrefix);
      return {
        request: (config: ng.IRequestConfig) => {
          const isGet = config.method === 'GET';
          const isInline = /^inline_/.test(config.url);
          const isApi = apiRequestRegex.test(config.url);
          if (isGet && !isInline && !isApi) {
            const isHtml = /\.html$/.test(config.url);
            const separator = (config.url.indexOf('?') >= 0) ? '&' : '?';
            config.url = config.url + separator + BuildId;
            if (L10n.language && isHtml) {
              // Only prefix the request with the language if:
              // - We have a valid language.
              // - The request is a GET for an html file.
              // - It's not a request for an inlined template.
              config.url = L10n.language + '/' + config.url;
            }
            config.url = StaticAssetsUrlPrefix + config.url;
          }
          return config;
        },
        responseError: (rejection: any) => {
          Tableau.Logger().error('http error ' + rejection.status + ' for request ' + rejection.config.url);
          if (rejection.status === 404) {
            if (rejection.config.url.indexOf('/error.html') >= 0) {
              // if we can't fetch the error page it's probably something pretty fundamental
              if (L10n.language && L10n.language !== 'en' && rejection.config.url.indexOf(L10n.language) === 0){
                Tableau.Logger().error("Your language setting '" + L10n.language + "' may be invalid.");
                // potential improvement - create a language service and just change the language to 'en' for a better experience
              }
              goToFatalErrorState($injector, 'Failed to load error handling page.');
            }
            else if (rejection.config.url.indexOf('messages.') >= 0) {
              // failed to get strings for this language - it will fall back to English in ServerService but if this is english...
              if (L10n.language === 'en') {
                goToFatalErrorState($injector, 'Failed to load strings for UI.');
              }
            }
          }
          return $q.reject(rejection);
        }
      };
    }]);

    // Angular does not support SCE on IE7-.
    // This browser detection matches the check that Angular performs internally.
    if (navigator.userAgent.match(/msie/i) && (<any>document).documentMode > 0 && (<any>document).documentMode < 8) {
      $sceProvider.enabled(false);
    }

    const siteUrl = (url: string) => siteUrlMatcherFactory.compile(url);

    // Create a redirect handler for a site url. See $urlRouterProvider documentation for details
    const siteUrlRedirect = (url: string) => ['$match', ($match: any) => siteUrl(url).format($match)];

    $urlRouterProvider
      .when(siteUrl('/groups/{id}'), siteUrlRedirect('/groups/{id}/users'))
      .when(siteUrl('/user/{domainName}/{username}'), siteUrlRedirect('/user/{domainName}/{username}/workbooks'))
      .when(siteUrl('/user/{domainName}/{username}/'), siteUrlRedirect('/user/{domainName}/{username}/workbooks'))
      .when(siteUrl('/tasks'),  siteUrlRedirect('/tasks/extractRefreshes'))
      .when('/server', '/server/status')
      .when('/server/user/{domain}/{username}', '/server/user/{domain}/{username}/settings')
      .when('/server/user/{domain}/{username}/', '/server/user/{domain}/{username}/settings')
      .when('/server/tasks',    '/server/tasks/extractRefreshes')
      .otherwise(($injector: ng.auto.IInjectorService): void => {
        // Handle nonsensical URLs
        if ($injector.get<ServerService>('ServerService').inSession()) {
          $injector.get<ErrorState>('ErrorState').go();
        } else {
          // If not signed in, remember the redirect so they get an error page after sign in
          const redirect = $injector.get<ng.ILocationService>('$location').url();
          $injector.get<ng.ui.IStateService>('$state').go('login.authenticate', {redirect: redirect});
        }
      });

    /**
     * State Configurations
     */

    // B147151. Ideally we would process and remove the saml query param earlier in the flow (i.e. run.ts), but
    // we can't change the url through angular's $location object then because that disrupts the bootstrapping
    // process. We would have to parse and modify the window.location directly, and that's a non-trivial task.
    const processAndRemoveSamlQueryParam = ['$location', 'PostLoginNotifications', (
      $location: ng.ILocationService,
      PostLoginNotifications: PostLoginNotifications
    ) => {
      const search = $location.search();
      if (_.has(search, UrlParamKeys.IsFromSaml)) {
        if (Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.IsFromSaml])) {
          PostLoginNotifications.enableNotifications();
        }
        $location.search(UrlParamKeys.IsFromSaml, null);
      }
    }];

    // Login

    $stateProvider
      .state('login', {
        abstract: true,
        template: '<div class="tb-column-flex-parent tb-fill"><tb:app-banner></tb:app-banner><div ui-view class="tb-flex-fill"></div></div>',
        data: {
          authLevel: AuthLevel.Anonymous
        },
        onEnter: processAndRemoveSamlQueryParam
      })
      .state('login.interstitial', {
        template: '<tb:signing-in></tb:signing-in>'
      })
      .state('login.authenticate', {
        url: '/signin?redirect&error&externalRedirect&site&closePopupWhenDone&disableAutoSignin&passwordResetSuccess&disableAppBanner&client_type',
        templateUrl: 'login.html',
        controller: LoginCtrl
      })
      .state('login.signedOut', {
        url: '/signedOut',
        templateUrl: 'signedOut.html',
        controller: SignedOutCtrl
      })
      .state('login.siteSamlSignedOut', {
        url: '/siteSamlSignedOut',
        templateUrl: 'siteSamlSignedOut.html',
        controller: SignedOutCtrl
      })
      .state('login.site', {
        url: '/site',
        templateUrl: 'loginSitePicker.html',
        data: {
          authLevel: AuthLevel.Authenticated
        },
        controller: LoginSitePickerCtrl
      })
      .state('login.init', {
        templateUrl: 'initialization.html',
        controller: InitializationCtrl
      })
      .state('login.untrusted', {
        templateUrl: 'untrusted.html'
      })
      .state('login.error', {
        url: '/error',
        templateUrl: 'errorNoSite.html'
      })
      .state('login.error.errorCode', {
        url: '/{errorCode}',
        templateUrl: 'error.html',
        controller: ErrorCtrl
      })
      .state('login.error.noSite', {
        templateUrl: 'error.html',
        params: [
          ErrorCtrl.StateParamKeys.DetailsLocKey,
          ErrorCtrl.StateParamKeys.RedirectState,
          ErrorCtrl.StateParamKeys.RedirectLocKey
        ],
        controller: ErrorCtrl
      })
      .state('login.error.auth', {
        url: '/signin/{errorCode}?redirectPath',
        templateUrl: 'errorSignin.html',
        controller: SignInErrorCtrl
      });

    //Password Reset
    $stateProvider
      .state('login.requestPasswordReset', {
        url: '/requestPasswordReset',
        templateUrl: 'requestPasswordReset.html',
        controller: RequestPasswordResetCtrl
      })
      .state('login.passwordReset', {
        url: '/resetPassword/{token}?username',
        templateUrl:'passwordReset.html',
        controller: PasswordResetCtrl
      });

    // PKCE
    $stateProvider
      .state('pkce', {
        data: {
          authLevel: AuthLevel.Anonymous
        },
        template: '<ui-view></ui-view>', // https://github.com/angular-ui/ui-router/issues/3049
      })
      .state('pkce.generateCodeChallenge', {
        url: '/pkce/generateCodeChallenge?appUriProtocol',
        controller: PKCEGenerateCodeChallengeCtrl,
        template: '<tb:signing-in></tb:signing-in>',
      })
      .state('pkce.getAuthorizationCode', {
        url: '/pkce/getAuthorizationCode?appUriProtocol&codeChallenge&codeChallengeMethod',
        controller: PKCEGetAuthorizationCodeCtrl,
        templateUrl: 'pkceGetAuthorizationCode.html',
      })
      .state('pkce.login', {
        url: '/pkce/login?authorizationCode&host',
        controller: PKCELoginCtrl,
      });

    // Connection Authentication
    $stateProvider
      .state('authenticate', {
        url: '/authenticate?path&attributes&vizqlSessionId&isPreview&closePopupWhenDone&disableSessionPing',
        templateUrl: 'authenticateConnection.html',
        resolve: {
          authInfo: [
            '$stateParams',                                    'ServerService',
            ($stateParams: ConnectionAuthenticationStateParams, serverService: ServerService) => {
              return serverService.sendRequest(new GetDataConnectionAuthInfoRequest($stateParams));
            }
          ]
        },
        controller: AuthenticateConnectionCtrl,
        data: {
          authLevel: AuthLevel.Anonymous
        }
      });

    // Main

    $stateProvider
      .state('main', {
        abstract: true,
        url: '',
        templateUrl: 'main.html',
        data: {
          authLevel: AuthLevel.Authenticated
        },
        onEnter: processAndRemoveSamlQueryParam,
        controller: MainCtrl
      });

    $stateProvider
      .state('main.site', {
        abstract: true,
        // site url is optional. Grab it if it's there.
        url: siteUrlMatcherFactory.compile(''),
        template: '<div ui-view></div>',
        onEnter: ['PostLoginNotifications', 'GettingStartedNotifications', 'MastheadNotifications', (
          PostLoginNotifications: PostLoginNotifications,
          GettingStartedNotifications: GettingStartedNotifications,
          MastheadNotifications: MastheadNotifications
        ) => {
          // See http://mytableau/display/devft/Vizportal+Online+Notifications+Spec
          PostLoginNotifications.showNotificationsIfEnabled()
            .finally(() => GettingStartedNotifications.showNotificationsIfEnabled())
            .finally(() => MastheadNotifications.showNotificationsIfEnabled());
        }]
      });

    $stateProvider
      .state('main.server', {
        abstract: true,
        url: '/server',
        template: '<div ui-view></div>',
        data: {
          authLevel: AuthLevel.ServerAdmin
        },
        resolve: {
          serverSettings: ['ServerSettings', (ServerSettings: ServerSettings) => ServerSettings.fetch()],
          siteSettingsForServerAdmin: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
            return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForServerAdmin());
          }]
        }
      })
      .state('main.server.status', {
        url: '/status',
        templateUrl: 'serverStatus.html',
        resolve: {
          hosts: ['ServerInfoService', (serverInfo: ServerInfoService) => serverInfo.fetch()],
          rebuildSearchIndexCurrentJob: ['RebuildSearchIndexService', (rebuildSearchIndexService: RebuildSearchIndexService) => {
            return rebuildSearchIndexService.handleCurrentJob();
          }]
        },
        controller: ServerStatusCtrl
      })
      .state('main.server.settings', {
        templateUrl: 'serverSettingsTabs.html',
        abstract: true,
        controller: ServerSettingsTabsCtrl,
      })
      .state('main.server.settings.serverSettings', {
        url: '/settings',
        templateUrl: 'serverSettings.html',
        data: {
          authLevel: AuthLevel.ServerAdmin
        },
        resolve: {
            serverSettings: ['ServerSettings', (ServerSettings: ServerSettings) => ServerSettings.fetch()],
            serverTimestamps: ['ServerSettings', (ServerSettings: ServerSettings) => ServerSettings.getTimestamps()],
            siteSettingsForServerAdmin: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
                return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForServerAdmin());
            }],
            siteSettings: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
                return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForSiteAdmin());
            }]
        },
          controller: ['$scope', 'serverSettings', 'serverTimestamps', 'siteSettingsForServerAdmin', 'siteSettings', (
            $scope: ISettingsCoordinatorCtrlScope,
            serverSettings: IEditableServerSettings,
            serverTimestamps: IServerTimestamps,
            siteSettingsForServerAdmin: ISiteSettingsForServerAdmin,
            siteSettings: ISiteSettingsForSiteAdmin
          ) => {
            $scope.serverSettingsOptions = serverSettings;
            $scope.serverTimestamps = serverTimestamps;
            $scope.siteSettingsForServerAdmin = siteSettingsForServerAdmin;
            $scope.siteSettings = siteSettings;
          }]
      })
      .state('main.server.settings.licenses', {
        url: '/licenses',
        templateUrl: 'serverSettingsLicensesTab.html',
        resolve: {
          licensingInfo: ['LicensesService', (licenses: LicensesService) => licenses.fetch()]
        },
        controller: LicensesCtrl
      })
      .state('main.server.settings.extensions', {
        url: '/extensions',
        templateUrl: 'siteSettingsShouldShowExtensions.html',
        data: {
          authLevel: AuthLevel.ServerAdmin
        },
        resolve: {
          siteSettingsForServerAdmin: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
            return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForServerAdmin());
          }],
          siteSettings: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
            return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentExtensionsSiteSettingsForSiteAdmin());
          }],
          extensionServerSettings: ['ServerSettings', (server: ServerSettings) => {
            return server.getExtensionsSettings();
          }]
        },
        controller: ['$scope', 'siteSettingsForServerAdmin', 'siteSettings', 'extensionServerSettings', (
            $scope: ISettingsCoordinatorCtrlScope,
            siteSettingsForServerAdmin: ISiteSettingsForServerAdmin,
            siteSettings: ISiteSettingsForSiteAdmin,
            extensionServerSettings: IExtensionsServerSettings
          ) => {
            $scope.siteSettingsForServerAdmin = siteSettingsForServerAdmin;
            $scope.siteSettings = siteSettings;
            $scope.extensionsServerSettings = extensionServerSettings;
          }]
      })
      .state('main.server.settings.addSite', {
            url: '/addSite',
            templateUrl: 'addSite.html',
            controller: AddSiteCtrl
      })
      .state('main.server.analysis', {
        url: '/analysis/{sheetUrlId}',
        templateUrl: 'adminVizViewer.html',
        data: {
          allowCollapsedTopBar: true
        },
        resolve: {
          workbookUrlId: () => 'tabbed_admin_views'
        },
        controller: AdminVizViewerCtrl
      })
      .state('main.server.sites', {
        abstract: true,
        templateUrl: 'sites.html',
        resolve: {
          place: ['SitesPlace', (SitesPlace: SitesPlace) => SitesPlace.create()],
        },
        controller: SitesPlaceCtrl
      });

    const contentSubplaceResolve: IContentSubplaceResolve = {
      subplace: ['place', (place: Place) => place.subplace('sites')],
      shouldFetchDetails: () => (() => true) // Fetches details by default
    };

    $stateProvider
      .state('main.server.sites.sites', {
        url: '/sites',
        templateUrl: 'dataGridSubplaceWithFilterPanel.html',
        controller: ContentSubplaceCtrl,
        resolve: contentSubplaceResolve
      });

    // Content: see contentConfig.ts

    // Users

    $stateProvider
      .state('main.server.users', {
        url: '/users',
        templateUrl: 'serverUsersPlace.html',
        data: {
          authLevel: AuthLevel.ServerAdmin
        },
        controller: ServerUsersPlaceCtrl
      });

    $stateProvider
      .state('main.site.users', {
        url: '/users',
        templateUrl: 'siteUsersPlace.html',
        data: {
          authLevel: AuthLevel.SiteAdmin
        },
        resolve: {
          canSessionUserManageUsers: ['ServerService', (serverService: ServerService) => serverService.canSessionUserManageUsers()],
          siteAuthenticationSettings: ['SiteAuthenticationSettingsService', (settingsService: SiteAuthenticationSettingsService) => settingsService.getSiteAuthenticationSettings()]
        },
        controller: SiteUsersPlaceCtrl
      });

    // Server User
    // Looking for Site User? It's in contentConfig.ts.

    // !!! ATTENTION !!!
    // If you changed the url scheme of user or user setting,
    // make sure to also update the user setting url generation code in:
    // \workgroup\vqlweb\scriptsharp\src\Authoring\ViewModels\AuthoringMastheadViewModel.cs

    $stateProvider
      .state('main.server.user', {
        abstract: true,
        url: '/user/:domainName/{username:.*}', // {username:.*} make sure username with forward slashes '/' are captured
        templateUrl: 'serverUserPlace.html',
        controller: ServerUserPlaceCtrl,
        resolve: {
          isSessionUserSelf: ['ServerService', (serverService: ServerService) => {
            return serverService.isSessionUserSelf();
          }],
          serverUser: ['ServerUsers', '$stateParams', (serverUsers: ServerUsers, $stateParams: UserStateParams) => {
            return serverUsers.getUserByUsernameAndDomain($stateParams.username, $stateParams.domainName);
          }],
          userSettings: ['UserSettingsActions', '$stateParams', (userSettingsActions: UserSettingsActions, $stateParams: UserStateParams) => {
            return userSettingsActions.fetchUserSettings($stateParams.username, $stateParams.domainName);
          }],
          userDoesNotExist: ['ErrorState', 'serverUser', 'ServerService', (errorState: ErrorState, serverUser: IUserInfo, server: ServerService) => {
            if (!serverUser) {// user doesn't exist on this server
              if (server.canSessionUserManageUsers()) {
                const usersErrorState: IErrorStateParams = {
                  detailsLocKey: 'userCannotBeAccessed',
                  redirectState: 'main.server.users',
                  redirectLocKey: 'backToUsers'
                };
                errorState.go(usersErrorState);
              } else {
                errorState.go();
              }
            }
          }]
        }
      })
      .state('main.server.user.settings', {
        url: '/settings',
        templateUrl: 'serverUserSettingsSubplace.html',
        controller: ServerUserSettingsSubplaceCtrl,
        resolve: {
          serverUser: ['ServerUsers', '$stateParams', (serverUsers: ServerUsers, $stateParams: UserStateParams) =>
            serverUsers.getUserByUsernameAndDomain($stateParams.username, $stateParams.domainName)
          ],
          userRefreshTokenCount: ['ServerUsers', '$stateParams', (serverUsers: ServerUsers, $stateParams: UserStateParams) =>
            serverUsers.getUserRefreshTokenCount($stateParams.username, $stateParams.domainName)
          ],
        }
      });

    // Groups

    $stateProvider
      .state('main.site.groups', {
        url: '/groups',
        templateUrl: 'groupsPlace.html',
        data: {
          authLevel: AuthLevel.SiteAdmin
        },
        controller: GroupsPlaceCtrl
      });

    // Group

    $stateProvider
      .state('main.site.group', {
        abstract: true,
        url: '/groups/:id',
        templateUrl: 'group.html',
        data: {
          authLevel: AuthLevel.SiteAdmin
        },
        resolve: {
          group: ['Groups', '$stateParams', (Groups: Groups, $stateParams: ng.ui.IStateParamsService) => {
            return Groups.getById($stateParams['id']);
          }],
          groupDoesNotExist: ['ErrorState', 'group', 'ServerService',
            (errorState: ErrorState, group: IGroup, server: ServerService) => {
            if (!group) {
              errorState.go({
                detailsLocKey: 'groupCannotBeAccessed',
                redirectState: 'main.site.groups',
                redirectLocKey: 'backToGroups'
              });
            }
          }]
        },
        controller: GroupPlaceCtrl
      })
      .state('main.site.group.users', {
        url: '/users',
        templateUrl: 'groupUsersSubplace.html',
        controller: GroupUsersSubplaceCtrl
      })
      .state('main.site.group.details', {
        url: '/details',
        templateUrl: 'groupDetails.html',
        resolve: {
          groupDetails: [
            'Groups', 'group', (Groups: Groups, group: IGroupInfo) => {
              return Groups.getGroupDetails(group.id);
            }],
          canSessionUserManageUsers: ['ServerService', (serverService: ServerService) => serverService.canSessionUserManageUsers()]
        },
        controller: GroupDetailsCtrl
      });

    // Schedules

    const serverStateProperties: ng.ui.IState = {
      data: {
        authLevel: AuthLevel.ServerAdmin
      },
      resolve: {
        siteId: (): string => null
      }
    };

    const siteStateProperties: ng.ui.IState = {
      data: {
        authLevel: AuthLevel.SiteAdmin
      },
      resolve: {
        siteId: ['ServerService', (serverService: ServerService) => serverService.getSessionInfoPromise().then(() => serverService.sessionSiteId())]
      }
    };

    const scheduleStates = (stateName: string, extraStateProperties: ng.ui.IState) => {

      // List state

      const scheduleListState: ng.ui.IState = {
          url: '/schedules',
          templateUrl: 'schedules.html',
          controller: SchedulesCtrl
        };

      $.extend(true, scheduleListState, extraStateProperties);

      $stateProvider.state(stateName + 's', scheduleListState);

      // Place state

      const scheduleState: ng.ui.IState = {
        abstract: true,
        resolve: {
          schedule: [
            'Schedules',          '$stateParams',                          'siteId',
            (Schedules: Schedules, $stateParams: ng.ui.IStateParamsService, siteId: string) => {
              return Schedules.getByIdForSite($stateParams['id'], siteId);
            }
          ],
          place: [
            'TaskPlace',          'siteId',       'schedule',
            (TaskPlace: TaskPlace, siteId: string, schedule: ISchedule) => {
              return TaskPlace.create(siteId, { schedule: schedule.id });
            }
          ]
        },
        url: '/schedules/:id',
        templateUrl: 'schedule.html',
        controller: SchedulePlaceCtrl
      };

      $.extend(true, scheduleState, extraStateProperties);

      $stateProvider.state(stateName, scheduleState);

      // Subplace states

      $stateProvider
        .state(stateName + '.details', {
          url: '/details',
          templateUrl: 'scheduleDetails.html',
          controller: setBrowserTitleCtrl('details')
        });

      SubplaceStateConfigHelper.addTaskSubstatesForSchedules($stateProvider, stateName);
    };

    scheduleStates('main.server.schedule', serverStateProperties);
    scheduleStates('main.site.schedule', siteStateProperties);

    // Tasks

    const taskState = (stateName: string, extraStateProperties: ng.ui.IState) => {
      const taskState = {
        abstract: true,
        url: '/tasks',
        templateUrl: 'tasks.html',
        controller: TasksCtrl,
        resolve: {
          place: [ 'TaskPlace', 'siteId', (TaskPlace: TaskPlace, siteId?: string) => TaskPlace.create(siteId) ]
        }
      };

      $.extend(true, taskState, extraStateProperties);

      $stateProvider.state(stateName, taskState);
    };

    taskState('main.server.tasks', serverStateProperties);
    SubplaceStateConfigHelper.addTaskSubstatesForSchedules($stateProvider, 'main.server.tasks');

    taskState('main.site.tasks', siteStateProperties);
    SubplaceStateConfigHelper.addTaskSubstatesForSchedules($stateProvider, 'main.site.tasks');
    SubplaceStateConfigHelper.addDataAlertsState($stateProvider, 'main.site.tasks');

    // BackgroundJobs
    const backgroundJobState = (stateName: string, extraStateProperties: ng.ui.IState) => {
      const backgroundJobListState: ng.ui.IState = {
        url: '/jobs',
        template: '<tb:connected-background-jobs-space props="backgroundJobSpaceProps" />',
        controller: BackgroundJobsCtrl,
        resolve: {
          // Return a failed promise if the job management is not enabled to prevent navigation to this area
          isJobManagementEnabled: ['ServerService', (ServerService: ServerService) => {
            return ServerService.isJobManagementEnabled() || $.Deferred().reject();
          }]
        }
      };

      $.extend(true, backgroundJobListState, extraStateProperties);

      $stateProvider.state(stateName, backgroundJobListState);
    };

    backgroundJobState('main.server.backgroundJobs', serverStateProperties);
    backgroundJobState('main.site.backgroundJobs', siteStateProperties);

    // Viz

    $stateProvider
      .state('main.site.viz', {
        url: '/views/*path',
        templateUrl: 'vizViewer.html',
        data: {
          allowCollapsedTopBar: true
        },
        controller: VizViewerCtrl
      });

    $stateProvider
      .state('main.site.addUserToDataAlert', {
        url: '/dataAlert/{dataAlertId}/addUser/{userId}',
        controller: AddUserToDataAlertCtrl,
        templateUrl: 'addRemoveUserFromDataAlert.html'
      })
      .state('main.site.removeUserFromDataAlert', {
        url: '/dataAlert/{dataAlertId}/removeUser/{userId}',
        controller: RemoveUserFromDataAlertCtrl,
        templateUrl: 'addRemoveUserFromDataAlert.html'
      });

    // Site Status/Analysis Viz

    $stateProvider
      .state('main.site.status', {
        url: '/status',
        templateUrl: 'siteStatus.html',
        data: {
          authLevel: AuthLevel.SiteAdmin
        },
        controller: AdminSiteStatusCtrl
      })
      .state('main.site.analysis', {
        url: '/analysis/{sheetUrlId}',
        templateUrl: 'adminVizViewer.html',
        data: {
          allowCollapsedTopBar: true,
          authLevel: AuthLevel.SiteAdmin
        },
        resolve: {
          workbookUrlId: () => 'tabbed_site_views'
        },
        controller: AdminVizViewerCtrl
      });

    // Workbook Revision Preview Viz

    $stateProvider
      .state('main.site.preview', {
        url: '/workbooks/{workbookUrlId}/history/{revisionNumber}',
        templateUrl: 'workbookRevisionVizViewer.html',
        controller: WorkbookRevisionVizViewerCtrl
      });
    $stateProvider
      .state('main.site.previewWithSpecificSheet', {
        url: '/workbooks/{workbookUrlId}/{sheetUrlId}/history/{revisionNumber}',
        templateUrl: 'workbookRevisionVizViewer.html',
        controller: WorkbookRevisionVizViewerCtrl
      });

    // Site Settings
    $stateProvider
      .state('main.site.settings', {
          templateUrl: 'siteSettingsTabs.html',
          abstract: true,
          controller: SiteSettingsTabsCtrl
        })
      .state('main.site.settings.siteSettings', {
          url: '/settings',
          templateUrl: 'siteSettings.html',
          data: {
            authLevel: AuthLevel.SiteAdmin
          },
          resolve: {
              serverSettings: ['ServerService', 'ServerSettings', (ServerService: ServerService, ServerSettings: ServerSettings) => {
                return ServerService.isSessionServerAdmin() ? ServerSettings.fetch() : null;
              }],
              siteSettings: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
                 return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForSiteAdmin());
              }],
              siteSettingsForServerAdmin: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
                  if (ServerService.isSessionServerAdmin()) {
                      return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForServerAdmin());
                  } else {
                      return null;
                  }
              }],
              siteAuthenticationSettings: ['SiteAuthenticationSettingsService', (service: SiteAuthenticationSettingsService) => {
                return service.getSiteAuthenticationSettings(/* refresh */ true);
              }]
          },
          controller: ['$scope', 'serverSettings', 'siteSettings', 'siteSettingsForServerAdmin', 'siteAuthenticationSettings', (
            $scope: ISettingsCoordinatorCtrlScope,
            serverSettings: IServerAdminSettings,
            siteSettings: ISiteSettingsForSiteAdmin,
            siteSettingsForServerAdmin: ISiteSettingsForServerAdmin,
            siteAuthenticationSettings: ISiteAuthenticationSettings
          ) => {
            $scope.serverSettings = serverSettings;
            $scope.siteSettings = siteSettings;
            $scope.siteSettingsForServerAdmin = siteSettingsForServerAdmin;
            $scope.siteAuthenticationSettings = siteAuthenticationSettings;
          }]
        })
      .state('main.site.settings.authentication', {
          url: '/authentication',
          templateUrl: 'siteSettingsAuthentication.html',
          controller: SiteAuthenticationSettingsCtrl,
          resolve: {
            siteAuthenticationSettings: ['SiteAuthenticationSettingsService', (service: SiteAuthenticationSettingsService) => {
              return service.getSiteAuthenticationSettings(/* refresh */ true);
            }],
            siteSettingsForSiteAdmin: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
               return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForSiteAdmin());
            }],
            allowTransition: ['SiteAuthenticationSettingsService', (service: SiteAuthenticationSettingsService, $q: ng.IQService) => {
              if (!service.isEnabled()) {
                return $q.reject(); // Trigger a $stateChangeError
              }
            }]
          },
          data: {
            authLevel: AuthLevel.SiteAdmin
          }
        })
      .state('main.site.settings.bridge', {
          url: '/bridge',
          templateUrl: 'siteSettingsShouldShowBridge.html',
          data: {
            authLevel: AuthLevel.SiteAdmin
          },
          resolve: {
              settings: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
                return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteBridgeSettingsForSiteAdmin());
            }]
          },
          controller: ['$scope', 'settings', (
            $scope: ISettingsCoordinatorCtrlScope,
            settings: ISiteSettingsForSiteAdmin
          ) => {  $scope.siteSettings = settings;  }]
        })
      .state('main.site.settings.extensions', {
          url: '/extensions',
          templateUrl: 'siteSettingsShouldShowExtensions.html',
          data: {
            authLevel: AuthLevel.SiteAdmin
          },
          resolve: {
            siteSettingsForServerAdmin: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
              return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentSiteSettingsForServerAdmin());
            }],
            siteSettings: ['ServerService', 'Sites', (ServerService: ServerService, Sites: Sites) => {
              return ServerService.getSessionInfoPromise().then(() => Sites.getCurrentExtensionsSiteSettingsForSiteAdmin());
            }],
            extensionServerSettings: ['ServerSettings', (server: ServerSettings) => {
              return server.getExtensionsSettings();
            }]
          },
          controller: ['$scope', 'siteSettingsForServerAdmin', 'siteSettings', 'extensionServerSettings', (
              $scope: ISettingsCoordinatorCtrlScope,
              siteSettingsForServerAdmin: ISiteSettingsForServerAdmin,
              siteSettings: ISiteSettingsForSiteAdmin,
              extensionServerSettings: IExtensionsServerSettings
            ) => {
              $scope.siteSettingsForServerAdmin = siteSettingsForServerAdmin;
              $scope.siteSettings = siteSettings;
              $scope.extensionsServerSettings = extensionServerSettings;
            }]
          });

    $stateProvider.state('main.site.catalog', {
      url: '/catalog',
      template: '<tb:catalog-browser-host />',
      abstract: true,
      resolve: {
        // Return a failed promise if the data catalog area is not enabled to prevent navigation to this area
        isDataCatalogAreaEnabled: ['ServerService', (ServerService: ServerService) => {
          return ServerService.catalogingOnSiteEnabled() || $.Deferred().reject();
        }]
      }
    })
    // Defining the path as a sub-state allows for the app to switch between different paths
    // in the catalog area (via its internal React Router) without unmounting and remounting the entire Catalog component.
    .state('main.site.catalog.subpath', {
      url: '/*path',
    });

    $urlRouterProvider.when(siteUrl('/catalog'), siteUrlRedirect('/catalog/'));

    // Error pages
    $stateProvider
      .state('main.error', {
        templateUrl: 'error.html',
        params: [
          ErrorCtrl.StateParamKeys.DetailsLocKey,
          ErrorCtrl.StateParamKeys.DetailsLinkifierModel,
          ErrorCtrl.StateParamKeys.RedirectState,
          ErrorCtrl.StateParamKeys.RedirectLocKey,
          ErrorCtrl.StateParamKeys.RedirectSiteUrlName
        ],
        controller: ErrorCtrl
      });


    $stateProvider
      .state('fatal', {
        url: '/fatalError',
        template: '<span style={"margin-left":20px}><H2>Fatal Error</H2></span>'
      });
  }
]);
